import {Box, Textarea} from '@primer/react'
import {useCallback, useRef, type FormEvent} from 'react'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {IconButtonWithTooltip} from '@github-ui/icon-button-with-tooltip'
import {PaperAirplaneIcon, SquareFillIcon} from '@primer/octicons-react'
import {usePlaygroundManager, usePlaygroundState, type ModelState} from '../../../utils/playground-manager'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {textFromMessageContent} from '../../../utils/message-content-helper'

import {
  PlaygroundChatAttachmentButton,
  PlaygroundChatAttachmentDrawer,
  PlaygroundChatAttachmentItem,
  PlaygroundChatAttachmentList,
  useChatAttachments,
} from './PlaygroundChatAttachments'

import styles from './PlaygroundChatInput.module.css'
import {supportInputAttachments, reachedMaxAttachments} from '../../../utils/image-validation'

// TODO: This code could be shared from copilot-chat (if exported)
function SubmitButton({isDisabled, onSubmit}: {isDisabled?: boolean; onSubmit: () => void}) {
  return (
    <IconButtonWithTooltip
      variant="invisible"
      size="small"
      onClick={onSubmit}
      icon={PaperAirplaneIcon}
      aria-label="Send now"
      disabled={isDisabled}
      label="Send now"
      tooltipDirection="w"
    />
  )
}

const StopButton = ({onSubmit}: {onSubmit: () => void}) => {
  return (
    <IconButtonWithTooltip
      variant="invisible"
      size="small"
      onClick={onSubmit}
      icon={SquareFillIcon}
      aria-label="Stop response"
      label="Stop"
      tooltipDirection="w"
    />
  )
}

export function PlaygroundChatInput({model, position}: {model: ModelState; position: number}) {
  const manager = usePlaygroundManager()
  const state = usePlaygroundState()

  const {chatInput, isLoading, chatClosed} = model
  const isSendingMessage = state.models.some(m => m.isLoading)

  const imageInputFeatureFlag = useFeatureFlag('project_neutron_image_input')
  const supportsAttachments = imageInputFeatureFlag && supportInputAttachments(model.catalogData)
  const attachmentDisabled = reachedMaxAttachments(model.messages, model.modelInputSchema)

  const chatAttachments = useChatAttachments()
  const hasAttachements = chatAttachments.attachments.length > 0
  const shouldShowAttachmentDrawer = supportsAttachments && (hasAttachements || chatAttachments.errorMessage)

  const MIN_HEIGHT = 44
  const MAX_HEIGHT = 300

  const text = textFromMessageContent(chatInput)
  const setText = useCallback(
    (newText: string) => {
      manager.setChatInput(state, newText)
    },
    [manager, state],
  )

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const textAreaScrollContainer = useRef<HTMLDivElement>(null)

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault()

    if (isLoading) return
    if (text.trim().length === 0) return

    const attachment = await chatAttachments.attachments.at(0)?.file.base64URI
    manager.sendChatMessage(state, text, attachment)
    setText('')
    chatAttachments.reset()
  }

  const handleInput = useCallback(
    (e: FormEvent<HTMLTextAreaElement>) => {
      setText((e.target as HTMLTextAreaElement).value)
    },
    [setText],
  )

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Escape') {
      e.preventDefault()
      manager.stopStreaming(position)
    }

    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      await handleSubmit()
    }
  }

  useLayoutEffect(() => {
    let tries = 0

    function adjustHeight() {
      if (!textAreaRef.current || !textAreaScrollContainer.current) return

      const currentScrollPosition = textAreaScrollContainer.current.scrollTop

      tries++
      if (textAreaRef.current.scrollHeight === 0 && tries < 10) {
        // because this is (often) in a Portal, useLayoutEffect() can run before we're inserted in the DOM, and we are
        // thus unable to get a correct scrollHeight. in that case, let's try again in a bit
        // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
        setTimeout(adjustHeight, 1)
      }

      textAreaRef.current.style.height = '0'
      const scrollHeight = textAreaRef.current.scrollHeight
      const containerHeight = Math.min(scrollHeight, MAX_HEIGHT)

      // textareaPreviewRef.current.style.height = `${scrollHeight}px`
      textAreaRef.current.style.height = `${scrollHeight}px`
      textAreaScrollContainer.current.style.height = `${containerHeight}px`
      textAreaScrollContainer.current.scrollTop = currentScrollPosition
    }

    adjustHeight()
  }, [text, textAreaRef])

  return (
    <Box
      ref={supportsAttachments ? chatAttachments.ref : null}
      className={styles.root}
      sx={{
        zIndex: 1,
        borderRadius: 'var(--borderRadius-medium)',
        boxShadow: '0 0 0 1px var(--borderColor-default, var(--color-border-default))',
        mr: 3,
      }}
    >
      {shouldShowAttachmentDrawer ? (
        <PlaygroundChatAttachmentDrawer>
          {chatAttachments.errorMessage ? (
            chatAttachments.errorMessage
          ) : (
            <PlaygroundChatAttachmentList>
              {chatAttachments.attachments.map(attachment => (
                <PlaygroundChatAttachmentItem key={attachment.file.key} onRemove={attachment.remove}>
                  {attachment.preview()}
                </PlaygroundChatAttachmentItem>
              ))}
            </PlaygroundChatAttachmentList>
          )}
        </PlaygroundChatAttachmentDrawer>
      ) : null}

      <Box
        className="copilot-chat-input"
        sx={{
          position: 'relative',
          borderRadius: 'var(--borderRadius-medium)',
          display: 'flex',
          alignItems: 'end',
          gap: 2,
          backgroundColor: 'canvas.default',
          px: 2,

          '> div': {flex: 1},

          ':focus-within .copilot-keyboard-shortcuts': {
            opacity: 1,
            visibility: 'visible',
          },

          ':has(textarea:focus)': {
            boxShadow: '0 0 0 2px var(--focus-outlineColor, var(--color-accent-emphasis))',
          },
        }}
      >
        <Box
          as="form"
          sx={{
            display: 'flex',
            alignItems: 'end',
            flexGrow: 1,
            '.copilot-chat-textarea::placeholder': {userSelect: 'none'},
          }}
          onSubmit={handleSubmit}
        >
          <Box
            ref={textAreaScrollContainer}
            className="copilot-chat-textarea-scroll-container"
            sx={{
              maxHeight: '30dvh',
              minHeight: `${MIN_HEIGHT}px`,
              overflowY: 'auto',
              position: 'relative',
              '> div:first-child': {position: 'static'},
              flexGrow: 1,
            }}
          >
            <Textarea
              // id={copilotChatTextAreaId}
              className="copilot-chat-textarea"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              role="textbox"
              aria-multiline="true"
              block
              ref={textAreaRef}
              resize="none"
              onKeyDown={handleKeyDown}
              disabled={chatClosed}
              // onScroll={synchronizeScroll}
              onInput={handleInput}
              aria-label="Prompt"
              placeholder="Type your prompt…"
              value={text}
              sx={{
                border: 'none',
                borderRadius: 0,
                borderTopLeftRadius: 'var(--borderRadius-medium)',
                borderTopRightRadius: 'var(--borderRadius-medium)',
                display: 'contents',
                '> textarea': {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  background: 'transparent',
                  caretColor: 'var(--fgColor-default, var(--color-fg-default))',
                  overflowY: 'hidden',
                  px: 2,
                  py: '12px',
                  verticalAlign: 'middle',
                  resize: 'none',
                  zIndex: 1,
                },
                ':focus-within': {
                  boxShadow: 'none',
                },
              }}
            />
          </Box>

          <span className="mb-2">
            {isLoading ? (
              <StopButton
                onSubmit={() => {
                  manager.stopStreaming(position)
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {supportsAttachments ? (
                  <PlaygroundChatAttachmentButton addFiles={chatAttachments.addFiles} disabled={attachmentDisabled} />
                ) : null}
                <SubmitButton
                  isDisabled={text.trim().length === 0 || isSendingMessage || chatClosed}
                  onSubmit={() => {
                    handleSubmit()
                  }}
                />
              </Box>
            )}
          </span>
        </Box>
      </Box>
    </Box>
  )
}

try{ SubmitButton.displayName ||= 'SubmitButton' } catch {}
try{ StopButton.displayName ||= 'StopButton' } catch {}
try{ PlaygroundChatInput.displayName ||= 'PlaygroundChatInput' } catch {}